import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Users, Star, Plus, Minus, Lock, Search, ShieldAlert } from "lucide-react";
import { api, apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Page, PageTitle, Card, GoldButton, GhostButton, Loader, Empty } from "../components/UI";
import { SimBadge } from "../components/Badges";

export default function NationalTeamBuilder() {
  const { hasRole } = useAuth();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [onlyRecommended, setOnlyRecommended] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/national-team/pool").then(({ data }) => setData(data)).catch((e) => {
      toast.error(apiErr(e)); setData({ error: true });
    });
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (path, player_id, label) => {
    try {
      const { data: res } = await api.post(`/admin/national-team/roster/${path}`, { player_id });
      if (res.validity) toast.success(label);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const publish = async () => {
    try {
      const { data: res } = await api.post("/admin/national-team/roster/publish", {});
      toast.success(`Roster locked & published — ${res.snapshot.count} players notified`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!hasRole("FEDERATION_ADMIN", "WSBF_ADMIN")) return <Page><Empty label="Federation admin access required" /></Page>;
  if (!data) return <Loader label="Loading player pool…" />;
  if (data.error) return <Page><Empty label="Could not load the national team builder." /></Page>;

  const v = data.validity;
  const nt = data.national_team;
  let pool = data.pool;
  if (q.trim()) pool = pool.filter((p) => (p.display_name + p.wsbf_player_id).toLowerCase().includes(q.trim().toLowerCase()));
  if (onlyRecommended) pool = pool.filter((p) => p.recommended);
  const selected = data.pool.filter((p) => p.in_roster);

  const stateColor = v.state === "FULL" ? "text-[#F0C72A]" : v.state === "VALID" ? "text-emerald-300" : "text-rose-300";

  return (
    <Page>
      <PageTitle eyebrow={`${nt?.name || "National Team"}`} title="Build National Team"
        subtitle="The system recommends by ArenaBook ranking. You select. Lock & publish a valid 15–25 squad to make it official." />

      {/* Roster counter */}
      <Card className="p-5 mb-6 flex flex-wrap items-center gap-4" data-testid="nt-roster-counter">
        <Users className="w-6 h-6 text-[#E5B842]" />
        <div className="flex-1 min-w-[180px]">
          <div className={`font-heading text-2xl font-black uppercase ${stateColor}`} data-testid="nt-validity-label">{v.label}</div>
          <div className="mt-2 h-2 rounded-full bg-[#070707] overflow-hidden">
            <div className="h-full" style={{
              width: `${Math.min(100, (v.count / v.max) * 100)}%`,
              background: v.valid ? "linear-gradient(90deg,#2E7D5B,#E5B842)" : "#9B1C31",
              transition: "width 0.25s ease",
            }} />
          </div>
        </div>
        <GoldButton data-testid="nt-publish-btn" disabled={!v.valid} onClick={publish}
          className={!v.valid ? "opacity-40 cursor-not-allowed" : ""}>
          <Lock className="w-4 h-4" /> Lock &amp; Publish Roster
        </GoldButton>
      </Card>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Pool */}
        <div className="lg:col-span-2">
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input data-testid="nt-pool-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter players…"
                className="w-full bg-[#0f0f0f] border border-[#2b2512] rounded-lg pl-9 pr-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-[#E5B842]/50" />
            </div>
            <button data-testid="nt-filter-recommended" onClick={() => setOnlyRecommended((x) => !x)}
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-mono uppercase tracking-wider border ${onlyRecommended ? "bg-[#E5B842] text-[#000000] border-[#E5B842]" : "bg-[#0f0f0f] text-slate-300 border-[#2b2512]"}`}
              style={{ transition: "background-color 0.15s ease" }}>
              <Star className="w-3.5 h-3.5" /> Recommended
            </button>
          </div>
          <Card className="overflow-hidden" data-testid="nt-pool">
            <div className="px-4 py-2 bg-[#070707] text-[10px] font-mono uppercase tracking-wider text-slate-500 flex items-center">
              <span className="flex-1">Player</span>
              <span className="w-16 text-right hidden sm:block">Rating</span>
              <span className="w-14 text-right hidden sm:block">Form</span>
              <span className="w-12 text-right">Caps</span>
              <span className="w-24" />
            </div>
            {pool.length === 0 ? <div className="p-5 text-slate-500 text-sm">No players match.</div> :
              pool.map((p) => (
                <div key={p.player_id} data-testid={`nt-pool-row-${p.wsbf_player_id}`}
                  className="px-4 py-2.5 border-t border-[#2b2512]/60 flex items-center text-sm gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Link to={`/player/${p.wsbf_player_id}`} className="text-slate-100 hover:text-[#E5B842] font-semibold truncate" style={{ transition: "color 0.15s ease" }}>{p.display_name}</Link>
                      {p.is_simulated && <SimBadge />}
                      {p.recommended && <span data-testid="nt-recommended-badge" className="px-1.5 py-0.5 rounded text-[9px] font-mono font-bold uppercase tracking-wider bg-[#E5B842]/15 text-[#E5B842] border border-[#E5B842]/30">Recommended</span>}
                    </div>
                    <div className="text-[11px] text-slate-500 font-mono">
                      {p.wsbf_player_id}{p.world_rank ? ` · World #${p.world_rank}` : ""}{p.club ? ` · ${p.club.short_name || p.club.name}` : ""}
                    </div>
                  </div>
                  <span className="w-16 text-right font-mono text-slate-300 hidden sm:block">{p.rating ?? "—"}</span>
                  <span className="w-14 text-right font-mono text-slate-400 hidden sm:block">{p.form_win_pct != null ? `${p.form_win_pct}%` : "—"}</span>
                  <span className="w-12 text-right font-heading font-bold text-[#E5B842]">{p.caps}</span>
                  <span className="w-24 text-right">
                    {p.in_roster ? (
                      <GhostButton data-testid={`nt-remove-${p.wsbf_player_id}`} onClick={() => act("remove", p.player_id, "Removed from squad")} className="!px-2 !py-1 text-xs">
                        <Minus className="w-3.5 h-3.5" /> Remove
                      </GhostButton>
                    ) : (
                      <GoldButton data-testid={`nt-add-${p.wsbf_player_id}`} onClick={() => act("add", p.player_id, "Added to squad")} className="!px-2 !py-1 text-xs">
                        <Plus className="w-3.5 h-3.5" /> Add
                      </GoldButton>
                    )}
                  </span>
                </div>
              ))}
          </Card>
        </div>

        {/* Selected squad */}
        <div>
          <h2 className="font-heading text-lg font-bold uppercase tracking-tight text-slate-50 mb-3 flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-[#E5B842]" /> Selected ({selected.length})
          </h2>
          <Card className="overflow-hidden" data-testid="nt-selected">
            {selected.length === 0 ? <div className="p-5 text-slate-500 text-sm">No players selected yet.</div> :
              selected.map((p) => (
                <div key={p.player_id} className="px-4 py-2 border-t border-[#2b2512]/60 first:border-t-0 flex items-center gap-2 text-sm">
                  <span className="flex-1 truncate text-slate-200">{p.display_name}</span>
                  <button data-testid={`nt-selected-remove-${p.wsbf_player_id}`} onClick={() => act("remove", p.player_id, "Removed from squad")} className="text-rose-400 hover:text-rose-300"><Minus className="w-4 h-4" /></button>
                </div>
              ))}
          </Card>
          <p className="mt-3 text-[11px] text-slate-500 leading-relaxed">
            Suspended or non-Greek players cannot be added — the platform enforces eligibility. Every change is recorded in the federation audit log.
          </p>
        </div>
      </div>
    </Page>
  );
}
